'use client';
import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import Spinner from '../../components/Spinner';
import { OPERATORS } from '../../constants/constants';
import { useSearchParams } from 'next/navigation';
import { paginateArticles } from '@/utils/articles/client';
import { Badge, IconButton, TextField } from '@mui/material';
import FilterBar from '../../components/Select/FilterBar';
import SummaryArticleCard from '@/components/ArticleCards/SummaryCard';
import IMAGE_SIZE from '@/constants/image-size.constants';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import FilterDrawer from '@/components/Select/FilterDrawer';
import { FilterAltSharp } from '@mui/icons-material';
import { EMPTY_FILTER } from '@/constants/article.constants';
import type { TypeArticleWithoutUnresolvableLinksResponse } from '@/contentful/types';

const PAGE_SIZE = 12;

type SearchProps = {
  pillars: ContentPillar[];
  sectors: ContentPillar[];
  locations: ContentPillar[];
  contentCategories: ContentPillar[];
};

const Search = ({
  pillars,
  sectors,
  locations,
  contentCategories
}: SearchProps) => {
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>(
    searchParams?.get('text') ?? ''
  );
  const [emptySearchText, setEmptySearchText] = useState<string>(
    'Search for articles'
  );
  const [isReset, setIsReset] = useState<boolean>(false);
  const [filter, setFilter] = useState<ArticleFilter>(EMPTY_FILTER);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const [categoryOperator, setCategoryOperator] = useState<string>(
    OPERATORS.AND
  );
  const [articles, setArticles] = useState<
    TypeArticleWithoutUnresolvableLinksResponse[]
  >([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [foundAll, setFoundAll] = useState<boolean>(false);

  const handleFilterChange = ({
    newFilter,
    operator
  }: {
    newFilter: ArticleFilter;
    operator: string;
  }) => {
    setFilter(newFilter);
    setCategoryOperator(operator);
    setCurrentPage(0);
    setFoundAll(false);
  };

  useEffect(() => {
    void handleSearchArticles(true, searchText);
  }, []);

  useEffect(() => {
    void handleSearchArticles(true, searchText);
  }, [filter]);

  useEffect(() => {
    const query = searchParams?.get('text');
    if (query !== null && query !== searchText) {
      setSearchText(query ?? '');
      setIsReset(true);
      void handleSearchArticles(true, query);
    }
  }, [searchParams]);

  const handleSearchArticles = async (reset = false, searchValue = '') => {
    if (searchValue) {
      if (
        (searchValue.length >= 2 && searchValue.trim().length > 0) ||
        searchValue === ''
      ) {
        setLoading(true);
        if (reset) {
          setArticles([]);
          setCurrentPage(0);
          setFoundAll(false);
        }
        const query: FilterQuery = {
          text: searchValue,
          start: reset ? 0 : currentPage * PAGE_SIZE,
          rows: PAGE_SIZE,
          categoryOperator
        };

        if (filter.pillar && filter.pillar.length > 0) {
          query.pillar = filter.pillar.join(',');
        }

        if (filter.sector && filter.sector.length > 0) {
          query.sector = filter.sector.join(',');
        }

        if (filter.location && filter.location.length > 0) {
          query.locationFilter = filter.location.join(',');
        }

        if (filter.contentCategory && filter.contentCategory.length > 0) {
          query.category = filter.contentCategory.join(',');
        }

        try {
          const newArticles = await paginateArticles(query);
          // setFoundAll(false);
          if (newArticles.length < PAGE_SIZE) {
            setFoundAll(true);
          }
          if (reset) {
            setArticles(newArticles);
            setFoundAll(false);
          } else {
            setArticles(articles.concat(newArticles));
          }
          setCurrentPage(currentPage + 1);
          if (newArticles.length === 0 && articles.length === 0) {
            setEmptySearchText('No results found');
          }
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
          setEmptySearchText('There was a problem searching for articles');
        }
      }
    }
  };

  useEffect(() => {
    void handleSearchArticles(isReset, searchText);
  }, [filter]);

  const search = (value: string) => {
    const url = new URL(window.location.toString());
    url.searchParams.set('text', value);
    window.history.pushState({}, '', url);
    setIsReset(true);
    setSearchText(value);
    void handleSearchArticles(true, value);
  };

  const filtered =
    filter.contentCategory.length > 0 ||
    filter.location.length > 0 ||
    filter.sector.length > 0 ||
    filter.pillar.length > 0;

  return (
    <div className='search-container'>
      <FilterDrawer
        open={mobileFilterOpen}
        filter={filter}
        handleFilterChange={handleFilterChange}
        clearFilters={() => {
          setFilter(EMPTY_FILTER);
          setMobileFilterOpen(false);
        }}
        closeFilter={() => setMobileFilterOpen(false)}
        pillars={pillars}
        sectors={sectors}
        locations={locations}
        contentCategories={contentCategories}
      />
      <div className='theme-container'>
        <div className='standard-page-main'>
          <div className='search__header'>
            <TextField
              className='search__input input--hide-outline'
              placeholder='Search The Urban Developer by...'
              value={searchText}
              onChange={(e) => search(e.target.value)}
              variant='standard'
            />
            <div className='filter-mobile'>
              <Badge
                color='primary'
                badgeContent={
                  filtered
                    ? filter.contentCategory.length +
                      filter.location.length +
                      filter.sector.length +
                      filter.pillar.length
                    : 0
                }
              >
                <IconButton
                  aria-label='Close'
                  onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                >
                  <FilterAltSharp sx={{ color: '#1c1c1c' }} />
                </IconButton>
              </Badge>
            </div>
            <div className='filter-desktop'>
              <FilterBar
                filter={filter}
                handleFilterChange={handleFilterChange}
                clearFilters={() => setFilter(EMPTY_FILTER)}
                pillars={pillars}
                sectors={sectors}
                locations={locations}
                contentCategories={contentCategories}
              />
            </div>
          </div>
          <div className='search__body'>
            <div className='search__main'>
              {articles.length > 0 ? (
                <>
                  <div className='category__body'>
                    {articles.map((article) => (
                      <SummaryArticleCard
                        key={article.fields.slug}
                        content={article}
                        imageSize={IMAGE_SIZE.ARTICLE_LIST}
                      />
                    ))}
                  </div>
                  {/* </div> */}
                  {!foundAll && articles.length > 0 ? (
                    <div className='search__load-more'>
                      <LoadMoreButton
                        onClick={() => handleSearchArticles(false, searchText)}
                        hasMore={!foundAll}
                        loading={loading}
                      />
                    </div>
                  ) : (
                    <p className='infinity-reached'>All results found</p>
                  )}
                </>
              ) : (
                <div className='search__empty'>
                  {loading ? (
                    <Spinner show={loading} size='normal' />
                  ) : (
                    <div>
                      <SearchIcon />
                      <h3>
                        {emptySearchText
                          ? emptySearchText
                          : 'Search for articles'}
                      </h3>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;
